import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { login, forgotPassword } from '../http/auth.js';
import styled from 'styled-components';
import { Alert } from '../components/Ui/Alert.jsx';
import { getCookie } from '../http/auth.js';
import logoSmall from '../assets/MCR-V-4.png';
import { isMobile, isAndroid, isIOS } from 'react-device-detect';
import { FaSafari, FaShareSquare, FaHome, FaChrome, FaAndroid } from 'react-icons/fa';
import { Row, FieldContainer } from '../components/StyledComponents/Forms/Forms.jsx';

const LoginContainer = styled.div`
  max-width: 550px;
  padding: 20px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  background: #fff;

  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background: #f0f0f0;
`;


const Title = styled.h2`
  color: #333;
  margin-bottom: 0.5em;
  font-weight: 600;
`;

const Subtitle = styled.p`
  color: #666;
  margin-top: 0;
  margin-bottom: 2em;
`;

const Form = styled.div`
  display: flex;
  flex-direction: column;
`;

const Input = styled.input`
  padding: 10px;
  margin: 10px 0;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 16px;
`;

const Button = styled.button`
  padding: 10px;
  background-color: #4a90e2;
  color: white;
  border: none;
  border-radius: 4px;
  margin-top: 20px;
  cursor: pointer;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    background-color: #357abd;
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 10px;
`;

const Label = styled.label`
  display: flex;
  align-items: center;

  > input[type='checkbox'] {
    margin-right: 10px;
  }
`;

const Link = styled.a`
  color: #4a90e2;
  cursor: pointer;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const BottomText = styled.p`
  margin-top: 1.5em;
  text-align: center;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  line-height: 15px;
`;

const HideInstructionButton = styled.button`
  background-color: #007aff;
  color: white;
  border: none;
  border-radius: 5px;
  padding: 8px 12px;
  font-size: 14px;
  cursor: pointer;
  margin-top: 10px;
  display: block;
  width: 100%;
  text-align: center;

  &:hover {
    background-color: #005bb5;
  }
`;

const PwaInfoFieldset = styled.fieldset`
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 15px;
  margin-top: 20px;
`;

const PwaInfoLegend = styled.legend`
  font-size: 16px;
  color: #333;
  padding: 0 10px;
`;

const PwaInfoText = styled.p`
  font-size: 14px;
  color: #555;
  margin: 10px 0;
`;

const Instructions = styled.div`
  margin-top: 20px;
  max-height: ${({ showInstructions }) => (showInstructions ? '300px' : '0')};
  overflow: hidden;
  transition: max-height 0.5s ease-in-out;
  background-color: #f9f9f9;
  border-radius: 8px;
  padding: ${({ showInstructions }) => (showInstructions ? '15px' : '0px 15px 0px 15px')};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const Instruction = styled.p`
  font-size: 14px;
  color: #555;
  margin: 10px 0;
  padding-left: 20px;
  position: relative;
  display: flex;
  align-items: center;
`;

const WideFieldContainer = styled(FieldContainer)`
  flex: 0.6;
`;

const NarrowFieldContainer = styled(FieldContainer)`
  flex: 0.4;
`;

const Spinner = styled.div`
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid transparent;
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

export default function Login() {
  const [rememberMe, setRememberMe] = useState(getCookie('rememberMe'));
  const [username, setUsername] = useState((rememberMe ? getCookie('username') : ''));
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallButton, setShowInstallButton] = useState(true);
  const [showInstructions, setShowInstructions] = useState(false);
  const [forgotPasswordSent, setForgotPasswordSent] = useState(false);
  const [forgotPasswordNotSet, setForgotPasswordNotSet] = useState(false);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const toggleInstructions = () => {
    if (isMobile) {
      setShowInstructions(!showInstructions);
    }
    else {
      handleInstallClick();
    }
  };

  useEffect(() => {
    const token = getCookie('username');
    if (token) {
      redirectToHome();
    }
  }, [navigate]);

  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.key === 'Enter') {
        handleLogin();
      }
    };

    document.addEventListener('keydown', handleKeyPress);

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [username, password]);

  useEffect(() => {
    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallButton(true);
    });
  }, []);

  const setCookie = (name, value, expiration) => {
    const expires = new Date(expiration).toUTCString();
    document.cookie = `${name}=${value}; expires=${expires}; path=/; SameSite=Strict`;
  };

  const redirectToHome = () => { navigate('/home'); }
  const redirectToRegistration = () => { navigate('/register'); }

  async function clearServiceWorkerCache() {
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(cache => caches.delete(cache)));
      console.log('Cache storage cleared.');
    }
  }

  const handleForgotPassword = async () => {
    setForgotPasswordSent(false);

    if (!username) {
      setForgotPasswordNotSet(true);
      return;
    }

    setForgotPasswordNotSet(false);
    debugger;
    try {
      const res = await forgotPassword(username);
      setForgotPasswordSent(true);
    } catch (e) {

    } finally {
      setForgotPasswordSent(true);
    }
  }

  const handleLogin = async () => {
    setLoading(true);
    try {
      const res = await login(username, password);
      //setCookie('token', res.token, res.expiration);

      if(res.success === false) {
        throw new Error(res.message || 'Login failed');
      }
      setCookie('accountType', res.UserType, res.expiration);
      setError(null);
      setLoading(false);
      await clearServiceWorkerCache();

      const thirtyDays = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
      setCookie('username', username, thirtyDays);
      redirectToHome();
    } catch (e) {
      setError(e);
      setLoading(false);
    }
  };

  const handleInstallClick = () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      deferredPrompt.userChoice.then((choiceResult) => {
        if (choiceResult.outcome === 'accepted') {
          console.log('User accepted the install prompt');
        } else {
          console.log('User dismissed the install prompt');
        }
        setDeferredPrompt(null);
        setShowInstallButton(false);
      });
    }
  };

  const queryParams = new URLSearchParams(location.search);
  const expired = queryParams.get('expired');
  const registered = queryParams.get('registered');
  const passwordReset = queryParams.get('reset');

  return (
    <Wrapper>
      <LoginContainer>
        <Container>
          <img src={isMobile ? logoSmall : logoSmall} alt="Logo" />
          <Title>MCR EyeTicket</Title>
          <Subtitle>Please enter your credentials to sign in</Subtitle>
        </Container>
        <Form>
          <Input
            name="Email"
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="Email"
            autoComplete="username"
          />
          <Input
            name="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            autoComplete="current-password"
          />
          {forgotPasswordSent && <Alert type='success'>An email has been sent to you with instructions on how to reset your password</Alert>}
          {forgotPasswordNotSet && <Alert type='warning'>Please enter an email and then select 'Forgot Password'</Alert>}
          {error && <Alert type="error">{error.message}</Alert>}
          {expired && <Alert type="warning">Your session has expired, please login again</Alert>}
          {registered === 'success' && <Alert type="success">You have successfully registered for an account. We will be in touch shortly</Alert>}
          {registered === 'failed' && <Alert type="error">An error occurred during registration, please contact MCR Safety</Alert>}
          {passwordReset === 'true' && <Alert type="success">If the given email is associated with an account, you will receive a password reset email shortly.</Alert>}
          <Row>
            <NarrowFieldContainer>
              <Button onClick={() => navigate('/forgot-password')}>Forgot Password?</Button>
            </NarrowFieldContainer>
            <WideFieldContainer>
              <Button onClick={handleLogin} disabled={loading}>
                {loading && <Spinner />}
                {loading ? 'Signing In...' : 'Sign In'}
              </Button>
            </WideFieldContainer>
          </Row>
        </Form>
        <BottomText>
          Don't have an account yet? <Link onClick={() => { redirectToRegistration(); }}>Sign up</Link>
        </BottomText>
        {showInstallButton && (
          <PwaInfoFieldset>
            <PwaInfoLegend>PWA Information</PwaInfoLegend>
            <PwaInfoText>
              This site has app functionality. Install it on your device for an enhanced experience and easy access.
            </PwaInfoText>
            <HideInstructionButton onClick={toggleInstructions}>
              {isMobile ? 'Add To Homescreen' : 'Install App'}
            </HideInstructionButton>
            {isIOS &&
              <Instructions showInstructions={showInstructions}>

                <Instruction>
                  <FaSafari style={{ marginRight: '10px' }} />
                  1) Open in Safari browser
                </Instruction>
                <Instruction>
                  <FaShareSquare style={{ marginRight: '10px' }} />
                  2) Press Share in Navigation bar
                </Instruction>
                <Instruction>
                  <FaHome style={{ marginRight: '10px' }} />
                  3) Press Add to Home Screen
                </Instruction>
              </Instructions>}
            {isAndroid && (
              <Instructions showInstructions={showInstructions}>
                <Instruction>
                  <FaChrome style={{ marginRight: '10px' }} />
                  1) Open in Chrome browser
                </Instruction>
                <Instruction>
                  <FaShareSquare style={{ marginRight: '10px' }} />
                  2) Press Menu (three dots) in Navigation bar
                </Instruction>
                <Instruction>
                  <FaAndroid style={{ marginRight: '10px' }} />
                  3) Press Add to Home Screen
                </Instruction>
              </Instructions>
            )}
          </PwaInfoFieldset>
        )}
      </LoginContainer>
    </Wrapper>
  );
}
